import React, { JSX } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";

export interface ITableData {
  id?: number;
  deleted?: string;
}

export interface TableProps<T extends ITableData> {
  columns: ColumnDef<T>[];
  data: T[];
  onClickRow?: (row: T) => void;
  selectedIds?: number[];
}

const Table = <T extends ITableData>({
  columns,
  data,
  onClickRow,
  selectedIds,
}: TableProps<T>): JSX.Element => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full shadow-sm">
        <thead className="!rounded-tl-xl !rounded-tr-xl bg-[#f9f9f9]">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  className="whitespace-nowrap px-[6px] py-[15px] text-left text-sm font-semibold uppercase tracking-wider text-[#636363]"
                  key={header.id}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody
          className={classNames("divide-y divide-gray-200 bg-white", {
            "cursor-pointer": !!onClickRow,
          })}
        >
          {table.getRowModel().rows.map((row, rowIndex) => {
            return (
              <tr
                key={row.id}
                onClick={() => {
                  onClickRow?.(row.original);
                }}
                className={classNames("border-transparent hover:bg-gray-100", {
                  "border-b border-gray-300":
                    rowIndex === table.getRowModel().rows.length - 1,
                  // @ts-ignore
                  "!rounded-[10px] !border-[#73b76f] !pb-4":
                    selectedIds?.includes(row?.original?.id),
                })}
              >
                {row.getVisibleCells().map((cell) => {
                  return (
                    <td
                      key={cell.id}
                      onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
                        if (cell.column.id === "actions") {
                          e.stopPropagation();
                        }
                      }}
                      className="px-[6px] py-4 text-sm text-[#121212]"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export { Table };
